import { z } from '@hono/zod-openapi'

// 業者カテゴリ
export const CONTRACTOR_CATEGORY_GENERAL = 'general'
export const CONTRACTOR_CATEGORY_INTERIOR = 'interior'
export const CONTRACTOR_CATEGORY_PLUMBING = 'plumbing'
export const CONTRACTOR_CATEGORY_WATER_HEATER = 'water_heater'
export const CONTRACTOR_CATEGORY_AIR_CONDITIONER = 'air_conditioner'
export const CONTRACTOR_CATEGORY_OTHER_APPLIANCES = 'other_appliances'
export const CONTRACTOR_CATEGORY_ELECTRICAL = 'electrical'

export const CONTRACTOR_CATEGORIES = [
  { key: CONTRACTOR_CATEGORY_GENERAL, value: '全般' },
  { key: CONTRACTOR_CATEGORY_INTERIOR, value: '内装・建具' },
  { key: CONTRACTOR_CATEGORY_PLUMBING, value: '水回り関連' },
  { key: CONTRACTOR_CATEGORY_WATER_HEATER, value: '給湯器' },
  { key: CONTRACTOR_CATEGORY_AIR_CONDITIONER, value: 'エアコン' },
  { key: CONTRACTOR_CATEGORY_OTHER_APPLIANCES, value: 'その他電化製品' },
  { key: CONTRACTOR_CATEGORY_ELECTRICAL, value: '電気設備関連' },
] as const

export const contractorCategoryZ = z.enum([
  CONTRACTOR_CATEGORY_GENERAL,
  CONTRACTOR_CATEGORY_INTERIOR,
  CONTRACTOR_CATEGORY_PLUMBING,
  CONTRACTOR_CATEGORY_WATER_HEATER,
  CONTRACTOR_CATEGORY_AIR_CONDITIONER,
  CONTRACTOR_CATEGORY_OTHER_APPLIANCES,
  CONTRACTOR_CATEGORY_ELECTRICAL,
])
export type ContractorCategory = z.infer<typeof contractorCategoryZ>

// 手配上限金額オプション
export const ARRANGEMENT_LIMIT_ALL_ESTIMATE = 'all_estimate'
export const ARRANGEMENT_LIMIT_30000 = '30000'
export const ARRANGEMENT_LIMIT_50000 = '50000'
export const ARRANGEMENT_LIMIT_70000 = '70000'
export const ARRANGEMENT_LIMIT_100000 = '100000'
export const ARRANGEMENT_LIMIT_CONFIRM_REQUIRED = 'confirm_required'

export const ARRANGEMENT_LIMIT_OPTIONS = [
  { key: ARRANGEMENT_LIMIT_ALL_ESTIMATE, value: '全件見積り依頼' },
  { key: ARRANGEMENT_LIMIT_30000, value: '3万円（税抜）' },
  { key: ARRANGEMENT_LIMIT_50000, value: '5万円（税抜）' },
  { key: ARRANGEMENT_LIMIT_70000, value: '7万円（税抜）' },
  { key: ARRANGEMENT_LIMIT_100000, value: '10万円（税抜）' },
  {
    key: ARRANGEMENT_LIMIT_CONFIRM_REQUIRED,
    value:
      '手配前に対応確認が必要（オーナー様より都度上限金額をご指示いただく必要があります）',
  },
] as const

export const arrangementLimitZ = z.enum([
  ARRANGEMENT_LIMIT_ALL_ESTIMATE,
  ARRANGEMENT_LIMIT_30000,
  ARRANGEMENT_LIMIT_50000,
  ARRANGEMENT_LIMIT_70000,
  ARRANGEMENT_LIMIT_100000,
  ARRANGEMENT_LIMIT_CONFIRM_REQUIRED,
])
export type ArrangementLimit = z.infer<typeof arrangementLimitZ>

// エージェントコール業者手配承諾
export const AGENTCALL_CONSENT_AGREE = 'agree'
export const AGENTCALL_CONSENT_DISAGREE = 'disagree'

export const AGENTCALL_CONSENT_OPTIONS = [
  { key: AGENTCALL_CONSENT_AGREE, value: '承諾する' },
  {
    key: AGENTCALL_CONSENT_DISAGREE,
    value: '承諾しない（都度、オペレーションセンターより手配先確認を行います）',
  },
] as const

export const agentCallConsentZ = z.enum([
  AGENTCALL_CONSENT_AGREE,
  AGENTCALL_CONSENT_DISAGREE,
])
export type AgentCallConsent = z.infer<typeof agentCallConsentZ>

// 指定業者の有無
export const DESIGNATED_CONTRACTOR_EXISTS = 'exists'
export const DESIGNATED_CONTRACTOR_NONE = 'none'

export const DESIGNATED_CONTRACTOR_OPTIONS = [
  { key: DESIGNATED_CONTRACTOR_EXISTS, value: '指定業者あり' },
  { key: DESIGNATED_CONTRACTOR_NONE, value: '指定業者なし' },
] as const

export const designatedContractorStatusZ = z.enum([
  DESIGNATED_CONTRACTOR_EXISTS,
  DESIGNATED_CONTRACTOR_NONE,
])
export type DesignatedContractorStatus = z.infer<
  typeof designatedContractorStatusZ
>

// 指定業者情報
export const designatedContractorZ = z.object({
  id: z.number().optional(),
  name: z.string().min(1, '業者名は必須です'),
  contactPerson: z.string().optional(),
  categories: z
    .array(contractorCategoryZ)
    .min(1, 'カテゴリを1つ以上選択してください'),
  otherCategory: z.string().optional(),
  contact: z.string().min(1, '連絡先は必須です'),
  requestMethod: z.string().min(1, '依頼方法は必須です'),
  address: z.string().optional(),
  note: z.string().optional(),
})
export type DesignatedContractor = z.infer<typeof designatedContractorZ>

// 指定業者情報（フォーム用：バリデーションはsuperRefineで行う）
const designatedContractorFormZ = z.object({
  id: z.number().optional(),
  name: z.string(),
  contactPerson: z.string().optional(),
  categories: z.array(z.string()),
  otherCategory: z.string().optional(),
  contact: z.string(),
  requestMethod: z.string(),
  address: z.string().optional(),
  note: z.string().optional(),
})

// 運用ルール設定
export const operationalRuleZ = z
  .object({
    agentcallConsent: agentCallConsentZ,
    designatedContractorStatus: designatedContractorStatusZ,
    designatedContractors: z.array(designatedContractorFormZ),
    arrangementLimit: arrangementLimitZ,
  })
  .superRefine((data, ctx) => {
    // 「指定業者あり」の場合のみバリデーションを実行
    if (data.designatedContractorStatus === DESIGNATED_CONTRACTOR_EXISTS) {
      data.designatedContractors.forEach((contractor, index) => {
        if (!contractor.name || contractor.name.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '業者名は必須です',
            path: ['designatedContractors', index, 'name'],
          })
        }
        if (!contractor.categories || contractor.categories.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'カテゴリを1つ以上選択してください',
            path: ['designatedContractors', index, 'categories'],
          })
        }
        if (!contractor.contact || contractor.contact.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '連絡先は必須です',
            path: ['designatedContractors', index, 'contact'],
          })
        }
        if (
          !contractor.requestMethod ||
          contractor.requestMethod.trim() === ''
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '依頼方法は必須です',
            path: ['designatedContractors', index, 'requestMethod'],
          })
        }
      })
    }
  })
export type OperationalRule = z.infer<typeof operationalRuleZ>
